import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import WalletCashIcon from '@/assets/icons/wallet-cash';
import WalletGoldIcon from '@/assets/icons/wallet-gold';
import { Button } from '@/components/ButtonComponent';
import { Text } from '@/components/TextComponent';
import { Box, Column, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useCountdownSeconds } from '@/hooks/use-countdown';
import { logger } from '@/utils/logging';
import { toCoinsCurrency, toSocialCurrency } from '@/utils/numeric/currency';

import { useClaimTopUp, useTopUp } from '../hooks/use-top-up';
import { CountdownTimer } from './CountdownTimer';

const ICON_SIZE = 16;

export const DailyBonusSection = () => {
    const { t } = useTranslation(['wallet', 'sweeps']);
    const { data, isLoading } = useTopUp();
    const { mutate } = useClaimTopUp();
    const [claimEnabled, setClaimEnabled] = useState(false);
    const [nextTopUpDate, setNextTopUpDate] = useState(
        data?.next_top_up_at ? new Date(data.next_top_up_at) : new Date()
    );

    const countdownSeconds = useCountdownSeconds(nextTopUpDate);
    const cashTopUpAmount = data?.cash_top_up_amount || 0;

    useEffect(() => {
        if (data && data.next_top_up_at) {
            const nextTopUpTime = new Date(data.next_top_up_at);
            const currentTime = data.current_timestamp ? new Date(data.current_timestamp) : new Date();
            setClaimEnabled(currentTime >= nextTopUpTime);
            setNextTopUpDate(nextTopUpTime);
        }
    }, [data]);

    const handleClaim = useCallback(() => {
        mutate('daily_top_up', {
            onSuccess: response => {
                setClaimEnabled(false);
                const nextTopUp = response?.data?.next_top_up_at
                    ? new Date(response.data.next_top_up_at)
                    : new Date(Date.now() + 24 * 60 * 60 * 1000);
                setNextTopUpDate(nextTopUp);
            },
            onError: error => {
                logger.debug('[Daily Bonus]', 'Error claiming daily bonus:', error);
            },
        });
    }, [mutate]);

    return (
        <Column gap="s24" mt="s24">
            <Row gap="s14" alignItems="center">
                <Box flex={1} height={1} backgroundColor={ds.colorToken.background.neutral.subtler} />
                <Text variant="bodySmall" letterSpacing={-0.08} color="neutral300">
                    {t('wallet:or_claim_free_daily_bonus')}
                </Text>
                <Box flex={1} height={1} backgroundColor={ds.colorToken.background.neutral.subtler} />
            </Row>
            <Column
                backgroundColor={ds.colorToken.background.neutral.subtler}
                borderColor={ds.colorToken.border.subtle}
                borderWidth={1}
                borderRadius="r20"
                padding="s16"
                gap="s16"
                alignItems="center"
            >
                {claimEnabled ? (
                    <>
                        <Row gap="s12" alignItems="center">
                            <Row gap="s4" alignItems="center">
                                <WalletGoldIcon size={ICON_SIZE} />
                                <Text variant="bodyMedium2" fontWeight="500" lineHeight={20}>
                                    {toCoinsCurrency(data?.coin_top_up_amount || 0)}
                                </Text>
                                <Text variant="bodyMedium2" fontWeight="500" lineHeight={20} color="neutral300">
                                    {t('sweeps:gold_coins_short')}
                                </Text>
                            </Row>
                            {cashTopUpAmount > 0 ? (
                                <>
                                    <Box
                                        width={1}
                                        height={ICON_SIZE}
                                        backgroundColor={ds.colorToken.background.neutral.subtle}
                                    />
                                    <Row gap="s4" alignItems="center">
                                        <WalletCashIcon size={ICON_SIZE} />
                                        <Text variant="bodyMedium2" fontWeight="500" lineHeight={20}>
                                            {toSocialCurrency(cashTopUpAmount)}
                                        </Text>
                                        <Text variant="bodyMedium2" fontWeight="500" lineHeight={20} color="neutral300">
                                            {t('sweeps:betr_cash_short')}
                                        </Text>
                                    </Row>
                                </>
                            ) : null}
                        </Row>
                        <Box width="100%">
                            <Button
                                label={t('wallet:claim_bonus')}
                                hierarchy="primary"
                                disabled={isLoading}
                                onPress={handleClaim}
                            />
                        </Box>
                    </>
                ) : (
                    <>
                        <Text variant="bodyMedium2" color="neutral300" textAlign="center">
                            {t('wallet:bonus_claimed')}
                        </Text>
                        <CountdownTimer seconds={countdownSeconds} />
                    </>
                )}
            </Column>
        </Column>
    );
};
